<template>
    <div class="switch-account-dropdown">
        <div class="switch-account-dropdown__sticky-header">
            <Heading
                variant="h3"
                :labelTitle="translations.title"
                :prefixColoredIcon="{ icon: 'profile', color: 'brand' }"
            />
            <CompassInput
                ref="searchInput"
                v-model="searchQuery"
                :placeholder="translations.placeholder"
                prefixIcon="search"
                :type="CompassEnums.InputType.SEARCH"
                class="switch-account-dropdown__search"
            />
        </div>
        <CompassMenuSubtitle :label="resultsLabel" />
        <CompassMenuItem
            v-for="account in filteredAccounts"
            :key="account.uuid"
            @click="$emit('select', account)"
        >
            <span class="switch-account-dropdown__label">
                <span
                    v-for="(segment, index) in getDisplaySegments(account.displayName, searchQuery)"
                    :key="index"
                    :class="{ 'switch-account-dropdown__highlight': segment.isMatch }"
                >
                    {{ segment.text }}
                </span>
            </span>
        </CompassMenuItem>
        <CompassEmptyState
            v-if="filteredAccounts.length === 0"
            illustrationPath="/ressources/logged/img/illustrations/search-no-results.svg"
            :title="translations.emptyTitle"
            :caption="translations.emptyCaption"
            class="switch-account-dropdown__empty-state"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref } from 'vue';
import type { Translator } from 'types/injects/translator.type';
import Heading from 'vcomponents/common/Heading/Heading.vue';

interface Account {
    uuid: string;
    externalUserId: number;
    externalUserType: string;
    customer?: { name?: string };
    externalAttributes?: { third_name?: string };
    switchUrl: string;
    displayName: string;
}

interface Props {
    accounts: Account[];
}

const props = defineProps<Props>();
defineEmits<{
    select: [account: Account];
}>();

const $t = inject('$t') as Translator;

const translations: Record<string, string> = {
    title: $t.__('common.labels.switch_account'),
    placeholder: $t.__('common.labels.search'),
    emptyTitle: $t.__('header.Nav.no_account_found'),
    emptyCaption: $t.__('header.Nav.no_account_found_caption'),
};

const searchQuery = ref('');
const searchInput = ref<{ setFocus: () => void } | null>(null);

const normalizeForSearch = (value: string): string => {
    return value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[._\-/\\]+/g, '')
        .replace(/\s+/g, ' ')
        .trim();
};

const buildSearchMap = (value: string): { normalized: string; originalIndices: number[] } => {
    let normalized = '';
    const originalIndices: number[] = [];
    let lastIsSpace = true;
    for (let i = 0; i < value.length; i++) {
        const decomposed = value[i].normalize('NFD');
        for (const sub of decomposed) {
            if (/[\u0300-\u036f]/.test(sub)) {
                continue;
            }
            if (/[._\-/\\]/.test(sub)) {
                continue;
            }
            const lower = sub.toLowerCase();
            if (/\s/.test(lower)) {
                if (lastIsSpace) {
                    continue;
                }
                normalized += ' ';
                originalIndices.push(i);
                lastIsSpace = true;
            } else {
                normalized += lower;
                originalIndices.push(i);
                lastIsSpace = false;
            }
        }
    }
    while (normalized.endsWith(' ')) {
        normalized = normalized.slice(0, -1);
        originalIndices.pop();
    }
    return { normalized, originalIndices };
};

const matchesQuery = (displayName: string, query: string): boolean => {
    const haystack = normalizeForSearch(displayName);
    const tokens = normalizeForSearch(query).split(' ').filter(Boolean);
    if (tokens.length === 0) {
        return true;
    }
    return tokens.every((token) => haystack.includes(token));
};

const getDisplaySegments = (text: string, query: string): { text: string; isMatch: boolean }[] => {
    if (!query) {
        return [{ text, isMatch: false }];
    }
    const { normalized, originalIndices } = buildSearchMap(text);
    const tokens = normalizeForSearch(query).split(' ').filter(Boolean);
    if (tokens.length === 0) {
        return [{ text, isMatch: false }];
    }
    const ranges: { start: number; end: number }[] = [];
    for (const token of tokens) {
        let pos = 0;
        while ((pos = normalized.indexOf(token, pos)) !== -1) {
            ranges.push({
                start: originalIndices[pos],
                end: originalIndices[pos + token.length - 1] + 1,
            });
            pos += token.length;
        }
    }
    if (ranges.length === 0) {
        return [{ text, isMatch: false }];
    }
    ranges.sort((a, b) => a.start - b.start);
    const merged: { start: number; end: number }[] = [];
    for (const range of ranges) {
        const last = merged[merged.length - 1];
        if (last && last.end >= range.start) {
            last.end = Math.max(last.end, range.end);
        } else {
            merged.push({ ...range });
        }
    }
    const segments: { text: string; isMatch: boolean }[] = [];
    let cursor = 0;
    for (const range of merged) {
        if (cursor < range.start) {
            segments.push({ text: text.slice(cursor, range.start), isMatch: false });
        }
        segments.push({ text: text.slice(range.start, range.end), isMatch: true });
        cursor = range.end;
    }
    if (cursor < text.length) {
        segments.push({ text: text.slice(cursor), isMatch: false });
    }
    return segments;
};

const filteredAccounts = computed<Account[]>(() => {
    if (!searchQuery.value) {
        return props.accounts;
    }
    return props.accounts.filter((account) => matchesQuery(account.displayName, searchQuery.value));
});

const resultsLabel = computed<string>(() =>
    $t.n__('header.Nav.all_accounts_count', null, filteredAccounts.value.length, {
        count: filteredAccounts.value.length,
    }),
);

onMounted(() => {
    setTimeout(() => {
        searchInput.value?.setFocus();
    }, 0);
});
</script>

<style lang="scss" scoped>
.switch-account-dropdown {
    &__search {
        width: 100%;
    }

    &__empty-state {
        padding: var(--spacing-3) var(--spacing-2);
    }
}
</style>

<style lang="scss">
.switch-account-dropdown__sticky-header {
    position: sticky;
    top: 0;
    z-index: 1;
    margin: 0 calc(var(--spacing-3) * -1);
    padding: var(--spacing-3) var(--spacing-3) var(--spacing-0);
    background-color: var(--colors-common-background-default);
}

.switch-account-dropdown__highlight {
    padding: 0 1px;
    border-radius: 2px;
    background-color: var(--colors-common-background-highlight-search);
    font-weight: 700;
}
</style>
